// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    REGISTER_AFFILIATE: '/auth/register-affiliate',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    VERIFY_EMAIL: '/auth/verify-email',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    CHANGE_PASSWORD: '/auth/change-password',
  },

  // Users
  USERS: {
    ME: '/users/me',
    UPDATE_PROFILE: '/users/me',
    LIST: '/users',
    GET: (id: string) => `/users/${id}`,
    CREATE: '/users',
    UPDATE: (id: string) => `/users/${id}`,
    DELETE: (id: string) => `/users/${id}`,
  },

  // Stores
  STORES: {
    LIST: '/stores',
    GET: (id: string) => `/stores/${id}`,
    CREATE: '/stores',
    UPDATE: (id: string) => `/stores/${id}`,
    DELETE: (id: string) => `/stores/${id}`,
    SETTINGS: (id: string) => `/stores/${id}/settings`,
    ANALYTICS: (id: string) => `/stores/${id}/analytics`,
    PUBLIC: '/stores/public', // Get store by subdomain
  },

  // Merchant Store Management
  MERCHANT: {
    STORE: '/stores/merchant/store', // Get/Update merchant's store
    PAYMENT_PROVIDERS: {
      LIST: '/merchant/channels',
      CONNECT: '/merchant/channels',
      TOGGLE: (id: string) => `/merchant/channels/${id}/verify`,
      DISCONNECT: (id: string) => `/merchant/channels/${id}`,
    },
    CHANNELS: {
      LIST: '/merchant/channels',
      BANKS: '/merchant/channels/banks',
      REGISTER: '/merchant/channels',
      VERIFY: (id: string) => `/merchant/channels/${id}/verify`,
      ACTIVATE: (id: string) => `/merchant/channels/${id}/activate`,
      DELETE: (id: string) => `/merchant/channels/${id}`,
    }
  },

  // Products
  PRODUCTS: {
    LIST: '/products',
    GET: (id: string) => `/products/${id}`,
    CREATE: '/products',
    UPDATE: (id: string) => `/products/${id}`,
    DELETE: (id: string) => `/products/${id}`,
    BULK_UPDATE: '/products/bulk-update',
    CATEGORIES: '/products/categories',
    SEARCH: '/products/search',
  },

  // Inventory
  INVENTORY: {
    LIST: '/inventory',
    GET: (id: string) => `/inventory/${id}`,
    UPDATE: (id: string) => `/inventory/${id}`,
    ADJUST: (id: string) => `/inventory/${id}/adjust`,
    HISTORY: (id: string) => `/inventory/${id}/history`,
    LOW_STOCK: '/inventory/low-stock',
  },

  // Orders
  ORDERS: {
    LIST: '/orders',
    GET: (id: string) => `/orders/${id}`,
    CREATE: '/orders',
    UPDATE: (id: string) => `/orders/${id}`,
    CANCEL: (id: string) => `/orders/${id}/cancel`,
    STATUS: (id: string) => `/orders/${id}/status`,
    TRACKING: (id: string) => `/orders/${id}/tracking`,
  },

  // Customers
  CUSTOMERS: {
    LIST: '/customers/merchant',
    GET: (id: string) => `/customers/${id}`,
    CREATE: '/customers',
    UPDATE: (id: string) => `/customers/${id}`,
    DELETE: (id: string) => `/customers/${id}`,
    ORDERS: (id: string) => `/customers/${id}/orders`,
  },

  // Cart
  CART: {
    GET: '/cart',
    ADD_ITEM: '/cart/items',
    UPDATE_ITEM: (itemId: string) => `/cart/items/${itemId}`,
    REMOVE_ITEM: (itemId: string) => `/cart/items/${itemId}`,
    CLEAR: '/cart/clear',
    APPLY_COUPON: '/cart/coupon',
  },

  // Payments
  PAYMENTS: {
    MPESA: '/payments/mpesa',
    MPESA_CALLBACK: '/payments/mpesa/callback',
    VERIFY: (id: string) => `/payments/${id}/verify`,
    REFUND: (id: string) => `/payments/${id}/refund`,
    HISTORY: '/payments/history',
  },

  // Analytics
  ANALYTICS: {
    DASHBOARD: '/analytics/dashboard',
    SALES: '/analytics/sales',
    PRODUCTS: '/analytics/products',
    CUSTOMERS: '/analytics/customers',
    REVENUE: '/analytics/revenue',
  },

  // Shipping
  SHIPPING: {
    ZONES: {
      LIST: (storeId: string) => `/shipping/zones/${storeId}`,
      CREATE: (storeId: string) => `/shipping/zones/${storeId}`,
      UPDATE: (zoneId: string) => `/shipping/zones/${zoneId}`,
      DELETE: (zoneId: string) => `/shipping/zones/${zoneId}`,
      BASE: '/shipping/zones'
    },
    RATES: {
      CREATE: (zoneId: string) => `/shipping/rates/${zoneId}`,
      UPDATE: (rateId: string) => `/shipping/rates/${rateId}`,
      DELETE: (rateId: string) => `/shipping/rates/${rateId}`,
      BASE: '/shipping/rates'
    },
    MERCHANT_ROUTES: {
      LIST: (storeId: string) => `/shipping/merchant-routes/${storeId}`,
      AVAILABLE_TOWNS: (storeId: string) => `/shipping/merchant-routes/${storeId}/available-towns`,
      CREATE: (storeId: string) => `/shipping/merchant-routes/${storeId}`,
      UPDATE: (routeId: string) => `/shipping/merchant-routes/route/${routeId}`,
      DELETE: (routeId: string) => `/shipping/merchant-routes/route/${routeId}`,
    },
    PROVIDERS: {
      LIST: (storeId: string) => `/stores/${storeId}/shipping/providers`,
      UPDATE: (storeId: string, providerId: string) => `/stores/${storeId}/shipping/providers/${providerId}`,
      TEST: (storeId: string, providerId: string) => `/stores/${storeId}/shipping/providers/${providerId}/test`,
    },
  },
  
  // Storefront specific endpoints
  STOREFRONT: {
    CART: {
      SYNC: '/storefront/cart/sync',
    }
  },

  // Webhooks
  WEBHOOKS: {
    LIST: '/webhooks',
    GET: (id: string) => `/webhooks/${id}`,
    CREATE: '/webhooks',
    UPDATE: (id: string) => `/webhooks/${id}`,
    DELETE: (id: string) => `/webhooks/${id}`,
    TEST: (id: string) => `/webhooks/${id}/test`,
  },

  // Meta Commerce
  META: {
    AUTH: '/meta/oauth/initiate',
    AUTH_CALLBACK: '/meta/oauth/callback',
    CATALOGS: '/meta/oauth/catalogs',
    SELECT_CATALOG: '/meta/oauth/select-catalog',
    CREATE_CATALOG: '/meta/oauth/create-catalog',
    SYNC_STATUS: '/meta/oauth/status',
    SYNC_PRODUCTS: '/meta/oauth/sync',
    DISCONNECT: '/meta/oauth/disconnect',
    LINK_WHATSAPP: '/meta/whatsapp/link',
    UNLINK_WHATSAPP: '/meta/whatsapp/unlink',
    LINK_INSTAGRAM: '/meta/oauth/link-instagram',
  },

  // Google Commerce
  GOOGLE: {
    AUTH_URL: '/google/oauth/auth-url',
    AUTH_CALLBACK: '/google/oauth/callback',
    ACCOUNTS: '/google/oauth/accounts',
    CONNECT: '/google/oauth/connect',
    STATUS: '/google/oauth/status',
    DISCONNECT: '/google/oauth/disconnect',
    SYNC_STATUS: '/google/sync-status',
    SYNC_NOW: '/google/sync-now',
  },

  // Billing & Credits Wallet
  BILLING: {
    OVERVIEW: '/billing/overview',
    TRANSACTIONS: '/billing/transactions',
    WALLET: '/billing/wallet',
    WALLET_TRANSACTIONS: '/billing/wallet/transactions',
    WALLET_TOPUP: '/billing/wallet/topup',
    WALLET_CREATE: '/billing/wallet/create',
    WALLET_ASSIGN_STORES: '/billing/wallet/assign-stores',
    WALLET_DELETE: (id: string) => `/billing/wallet/${id}`,
  },

  // Storefront Locations
  STOREFRONT_LOCATIONS: {
    PICKUP_POINTS: '/storefront/locations/pickup-points',
    COUNTIES: '/storefront/locations/counties',
    TOWNS: '/storefront/locations/towns',
    MARKETS: '/storefront/locations/markets',
  },
} as const;
